import {
	getFormProps,
	getInputProps,
	getSelectProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	type ActionFunctionArgs,
	data,
	Form,
	useActionData,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import {
	HelpTypeEnum,
	helpTypeLabels,
	MessageSchema,
	MessageTypeEnum,
	messageTypeLabels,
} from '#app/utils/message.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { createToastHeaders } from '#app/utils/toast.server.ts'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Contact Us | Steel City Backgammon' },
]

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)

	const submission = parseWithZod(formData, { schema: MessageSchema })

	if (submission.status !== 'success') {
		const toastHeaders = await createToastHeaders({
			title: 'Form Error',
			description: 'Please correct the highlighted fields.',
			type: 'error',
		})
		return data({ result: submission.reply() }, { headers: toastHeaders })
	}

	try {
		await prisma.message.create({
			data: submission.value,
		})
		void sendEmail({
			to: 'info@steelcitybg.com',
			from: 'Steel City Backgammon <info+contact@steelcitybg.com>',
			subject:
				`New contact form submission from ${submission.value.firstName} ${submission.value.lastName ?? ''}`.trim(),
			html: `
				<p><strong>Name:</strong> ${submission.value.firstName} ${submission.value.lastName ?? ''}</p>
				<p><strong>Email:</strong> ${submission.value.email}</p>
				<p><strong>Type:</strong> ${submission.value.messageType}</p>
				${submission.value.helpType ? `<p><strong>Help type:</strong> ${submission.value.helpType}</p>` : ''}
				${submission.value.message ? `<p><strong>Message:</strong> ${submission.value.message}</p>` : ''}
			`.trim(),
			text: [
				`Name: ${submission.value.firstName} ${submission.value.lastName ?? ''}`,
				`Email: ${submission.value.email}`,
				`Type: ${submission.value.messageType}`,
				submission.value.helpType
					? `Help type: ${submission.value.helpType}`
					: '',
				submission.value.message ? `Message: ${submission.value.message}` : '',
			]
				.filter(Boolean)
				.join('\n'),
		})
		const toastHeaders = await createToastHeaders({
			title: 'Message Sent',
			description: `Thanks, ${submission.value.firstName}! We've received your message and will get back to you shortly.`,
			type: 'success',
		})
		return data(
			{ result: submission.reply(), message: '' },
			{ headers: toastHeaders },
		)
	} catch (error) {
		console.error('Failed to save contact message:', error, submission.value)
		const toastHeaders = await createToastHeaders({
			title: 'Contact Message Failed',
			description: 'Something went wrong on our end. Please try again later.',
			type: 'error',
		})
		return data({ result: submission.reply() }, { headers: toastHeaders })
	}
}

export default function ContactRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'contact-form',
		constraint: getZodConstraint(MessageSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: MessageSchema })
		},
		shouldRevalidate: 'onInput',
	})

	const visibility = {
		helpOptions: fields.messageType.value === 'help',
	}

	return (
		<main className="mx-auto max-w-2xl flex-col place-items-center px-4">
			<h1 className="text-4xl text-black sm:text-4xl dark:text-white">
				Contact Us
			</h1>
			<p className="mt-2 text-center text-gray-500 dark:text-gray-400">
				Have a question about the club or want to help? Let us know here.
			</p>

			<div className="mt-10 w-full rounded-xl bg-slate-50 p-6 dark:bg-slate-800">
				<Form method="POST" {...getFormProps(form)}>
					<HoneypotInputs />

					<div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
						<Field
							labelProps={{ children: 'First Name' }}
							inputProps={getInputProps(fields.firstName, { type: 'text' })}
							errors={fields.firstName.errors}
						/>
						<Field
							labelProps={{ children: 'Last Name' }}
							inputProps={getInputProps(fields.lastName, { type: 'text' })}
							errors={fields.lastName.errors}
						/>
					</div>

					<Field
						labelProps={{ children: 'Email Address' }}
						inputProps={getInputProps(fields.email, { type: 'email' })}
						errors={fields.email.errors}
					/>

					<div>
						<label
							className="mb-2 text-sm font-medium"
							htmlFor={fields.messageType.id}
						>
							What brings you here?
						</label>
						<select
							{...getSelectProps(fields.messageType)}
							className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
						>
							<option value="">Select a topic...</option>
							{MessageTypeEnum.options.map((option) => (
								<option key={option} value={option}>
									{messageTypeLabels[option]}
								</option>
							))}
						</select>
						<ErrorList
							errors={fields.messageType.errors}
							id={fields.messageType.errorId}
						/>
					</div>

					{visibility['helpOptions'] && (
						<div className="mt-8">
							<label
								className="mb-1 block text-sm font-medium"
								htmlFor={fields.helpType.id}
							>
								Sweet! What did you have in mind?
							</label>
							<select
								{...getSelectProps(fields.helpType)}
								className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
							>
								<option value="">Help ideas...</option>
								{HelpTypeEnum.options.map((option) => (
									<option key={option} value={option}>
										{helpTypeLabels[option]}
									</option>
								))}
							</select>
							<ErrorList
								errors={fields.helpType.errors}
								id={fields.helpType.errorId}
							/>
						</div>
					)}

					<div className="mt-8">
						<label
							className="mb-1 block text-sm font-medium"
							htmlFor={fields.message.id}
						>
							Message
						</label>
						<textarea
							{...getTextareaProps(fields.message)}
							className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[120px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
						/>
						<ErrorList
							errors={fields.message.errors}
							id={fields.message.errorId}
						/>
					</div>

					<div className="mt-6 flex flex-col items-center">
						<StatusButton
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
							className="w-full sm:w-48"
						>
							Send Message
						</StatusButton>
						<ErrorList errors={form.errors} id={form.errorId} />
					</div>
				</Form>
			</div>
		</main>
	)
}
